import { get } from 'lodash';
import type { ReactNode } from 'react';
import { useContext } from 'react';
import {
    type Control,
    Controller,
    type FieldError,
    type FieldErrors,
    type FieldValues,
    type Path,
    type UseFormRegister,
} from 'react-hook-form';
import {
    BodyMediumRegular,
    DollarInput,
    FootnoteRegular,
    Input,
    Picker,
    RowListHasHeaderContext,
    XStack,
    YStack,
} from 'smoothie';

interface BaseControlledRowProps<TFieldValues extends FieldValues, TContext> {
    name: Path<TFieldValues>;
    control: Control<TFieldValues, TContext>;
    register: UseFormRegister<TFieldValues>;
    errors: FieldErrors<TFieldValues>;
    label: string;
    disabled?: boolean;
}

/**
 * Lays out a single row: label on the left, control on the right.
 * Background color follows the parent `RowList` (gray100 with header / gray200 without)
 * to match the visual style of `Row`. Errors render below the row.
 */
const FormRowFrame = ({
    label,
    disabled = false,
    isError = false,
    errorMessage,
    children,
}: {
    label: string;
    disabled?: boolean;
    isError?: boolean;
    errorMessage?: string;
    children: ReactNode;
}) => {
    const hasHeader = useContext(RowListHasHeaderContext);
    const backgroundColor = hasHeader ? '$gray100' : '$gray200';

    return (
        <YStack>
            <XStack
                backgroundColor={backgroundColor}
                paddingVertical={10}
                alignItems="center"
                gap={12}
            >
                <BodyMediumRegular
                    width="50%"
                    color={disabled ? '$gray800' : '$gray1200'}
                >
                    {label}
                </BodyMediumRegular>
                <YStack width="50%">{children}</YStack>
            </XStack>
            {isError && errorMessage != null && errorMessage !== '' && (
                <YStack
                    backgroundColor={backgroundColor}
                    paddingHorizontal={16}
                    paddingBottom={8}
                    alignItems="flex-end"
                >
                    <FootnoteRegular color="$red1100">
                        {errorMessage}
                    </FootnoteRegular>
                </YStack>
            )}
        </YStack>
    );
};

interface ControlledPickerRowProps<
    TFieldValues extends FieldValues,
    TContext,
> extends BaseControlledRowProps<TFieldValues, TContext> {
    options: Array<{ label: string; value: string; enabled?: boolean }>;
    placeholder?: string;
}

export const ControlledPickerRow = <
    TFieldValues extends FieldValues,
    TContext,
>({
    name,
    control,
    register,
    errors,
    label,
    options,
    placeholder,
    disabled = false,
}: ControlledPickerRowProps<TFieldValues, TContext>) => {
    const error = get(errors, name) as FieldError | undefined;
    const isError = error != null;

    return (
        <Controller
            name={name}
            control={control}
            rules={{ ...register }}
            render={({ field: { onChange, value } }) => (
                <FormRowFrame
                    label={label}
                    disabled={disabled}
                    isError={isError}
                    errorMessage={error?.message}
                >
                    <Picker
                        value={(value ?? '') as string}
                        onChange={onChange}
                        options={options}
                        placeholder={placeholder}
                        disabled={disabled}
                        size="tiny"
                    />
                </FormRowFrame>
            )}
        />
    );
};

interface ControlledInputRowProps<
    TFieldValues extends FieldValues,
    TContext,
> extends BaseControlledRowProps<TFieldValues, TContext> {
    placeholder?: string;
    formatValueOnChange?: (previousValue: string, nextValue: string) => string;
    textAlign?: 'left' | 'right';
}

export const ControlledInputRow = <TFieldValues extends FieldValues, TContext>({
    name,
    control,
    register,
    errors,
    label,
    placeholder,
    disabled = false,
    formatValueOnChange,
    textAlign = 'right',
}: ControlledInputRowProps<TFieldValues, TContext>) => {
    const error = get(errors, name) as FieldError | undefined;
    const isError = error != null;

    return (
        <Controller
            name={name}
            control={control}
            rules={{ ...register }}
            render={({ field: { onChange, onBlur, ref, value } }) => (
                <FormRowFrame
                    label={label}
                    disabled={disabled}
                    isError={isError}
                    errorMessage={error?.message}
                >
                    <Input
                        ref={ref}
                        value={(value ?? '') as string}
                        onChangeText={(text: string) => {
                            onChange(
                                formatValueOnChange?.(
                                    (value ?? '') as string,
                                    text,
                                ) ?? text,
                            );
                        }}
                        onBlur={onBlur}
                        invalid={isError}
                        disabled={disabled}
                        placeholder={placeholder}
                        size="tiny"
                        fontWeight="regular"
                        textAlign={textAlign}
                        containerProps={{ width: '100%' }}
                    />
                </FormRowFrame>
            )}
        />
    );
};

interface ControlledCurrencyRowProps<
    TFieldValues extends FieldValues,
    TContext,
> extends BaseControlledRowProps<TFieldValues, TContext> {
    placeholder?: string;
    /** Format new input as cents-only by default (no decimals) to match the goal panel design. */
    allowDecimals?: boolean;
    formatValueOnChange?: (previousValue: string, nextValue: string) => string;
}

export const ControlledCurrencyRow = <
    TFieldValues extends FieldValues,
    TContext,
>({
    name,
    control,
    register,
    errors,
    label,
    placeholder = '$',
    allowDecimals = false,
    disabled = false,
    formatValueOnChange,
}: ControlledCurrencyRowProps<TFieldValues, TContext>) => {
    const error = get(errors, name) as FieldError | undefined;
    const isError = error != null;

    return (
        <Controller
            name={name}
            control={control}
            rules={{ ...register }}
            render={({ field: { onChange, ref, value } }) => (
                <FormRowFrame
                    label={label}
                    disabled={disabled}
                    isError={isError}
                    errorMessage={error?.message}
                >
                    <DollarInput
                        ref={ref}
                        value={(value ?? '') as string}
                        onChangeText={(text: string) => {
                            onChange(
                                formatValueOnChange?.(
                                    (value ?? '') as string,
                                    text,
                                ) ?? text,
                            );
                        }}
                        invalid={isError}
                        disabled={disabled}
                        placeholder={placeholder}
                        allowDecimals={allowDecimals}
                        size="tiny"
                        fontWeight="regular"
                        textAlign="right"
                        containerProps={{ width: '100%' }}
                    />
                </FormRowFrame>
            )}
        />
    );
};

/**
 * A row whose right-hand control is provided externally. Useful when wiring up
 * `<Checkbox>` or other custom controls inside the row layout.
 */
interface FormControlRowProps {
    label: string;
    children: ReactNode;
    disabled?: boolean;
}

export const FormControlRow = ({
    label,
    children,
    disabled = false,
}: FormControlRowProps) => (
    <FormRowFrame label={label} disabled={disabled}>
        {children}
    </FormRowFrame>
);
